import { CircularProgress } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import type { BigNumberish } from 'ethers';
import { BigNumber, constants } from 'ethers';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router';
import type { Address } from 'viem';

import { useAuth } from '../../../auth';
import { AuctionComponent } from '../../../components/auction/Auction.component';
import { BidModal } from '../../../components/common/modal/BidModal.component';
import { ConnectWalletModal } from '../../../components/common/modal/connect-wallet';
import { OkModal } from '../../../components/common/modal/OkModal.component';
import { WonAuctionModal } from '../../../components/common/modal/WonAuctionModal.component';
import type { WalletDetails } from '../../../contexts';
import { ModalStateContext } from '../../../contexts/app-state-context/modal-state.context';
import { useWeb3State } from '../../../hooks';
import { useMarketplaceContract } from '../../../hooks/marketplace-contract.hooks';
import { useStarsContract } from '../../../hooks/stars-contract.hooks';
import { WEB3_CONFIG } from '../../../lib';
import { AssetType } from '../../../lib/graph/MogulMarketplace';
import type { Asset } from '../../../lib/opensea';
import { getMetadata } from '../../../lib/opensea';
import { AUCTION_HOUSE_ADDRESS } from '../../../lib/smart-contract-utils';
import styles from './AuctionPage.module.css';

const stopInterval = async (promise: Promise<NodeJS.Timeout | undefined>) => {
  const interval = await promise;

  if (!interval) {
    return;
  }

  clearInterval(interval);
};

const LIVE_STREAM = {
  link: 'https://www.youtube.com/embed/C-4Efre015Y',
  title: 'Proof of Origin Event & Burning',
  description:
    'Join famed Marvel and DC Comics artist Rob Prior, and Mogul, as Rob paints and destroys a one-of-a-kind piece of artwork to mint into an NFT that will be auctioned exclusively on the Mogul platform. Rob and Mogul are joined by industry titans in the blockchain, music, gaming and film industries to discuss the future of the industry, while witnessing the creation and destroying of a historic NFT',
};

const useStyles = makeStyles(() =>
  createStyles({ loader: { color: 'white' } }),
);

interface Props {
  NFTToPull?: string;
}

export const AuctionPage: React.FC<Props> = () => {
  const classes = useStyles();
  const { authenticated } = useAuth();
  const { walletDetails } = useWeb3State();
  const { setModal } = useContext(ModalStateContext);

  const marketplaceContract = useMarketplaceContract({
    type: AssetType.Erc1155,
    overrideAddress: AUCTION_HOUSE_ADDRESS[walletDetails.chain.type],
  });

  const starsContract = useStarsContract();

  const [loading, setLoading] = React.useState(true);
  const [auction, setAuction] = React.useState({
    auctionId: BigNumber.from(0),
    bid: '0',
    highestBidderAddress: '',
    isHighestBidder: false,
    winnerAddress: '',
    startDate: new Date(),
    endDate: new Date(),
  });

  const [metadata, setMetadata] = useState<Asset>({
    name: '',
    description: '',
    imageUrl: '',
    imagePreviewUrl: '',
    imageThumbnailUrl: '',
    imageOriginalUrl: '',
    animationUrl: '',
    animationOriginalUrl: '',
    owner: {
      username: '',
      profileImgUrl: '',
      address: '',
    },
  });

  const history = useHistory();

  useEffect(() => {
    if (!walletDetails.address || !marketplaceContract.initialized) {
      return;
    }

    const getAuction = async (
      walletAddress: Address,
      chainType: WalletDetails['chain']['type'],
      auctionId: BigNumber,
    ) => {
      const auctionRaw = await marketplaceContract.auctions(auctionId);

      if (!auctionRaw) {
        return;
      }

      const interval = marketplaceContract.startInterval(
        auctionId,
        walletAddress,
        setAuction,
      );

      void getMetadata(
        auctionRaw.tokenAddress,
        auctionRaw.tokenId,
        chainType,
      ).then(setMetadata);

      setAuction(prev => ({
        ...prev,
        auctionId,
        bid: auctionRaw.bidAmount,
        highestBidderAddress: auctionRaw.highestBid.bidder,
        isHighestBidder: auctionRaw.highestBid.bidder === walletAddress,
        winnerAddress: auctionRaw.highestBid.bidder,
        startDate: new Date(auctionRaw.startTime.mul(1000).mul(2).toNumber()),
        endDate: new Date(auctionRaw.endTime.mul(1000).toNumber()),
      }));

      setLoading(false);

      return interval;
    };

    const intervalPromise = getAuction(
      walletDetails.address,
      walletDetails.chain.type,
      BigNumber.from(0),
    );

    return () => void stopInterval(intervalPromise);
  }, [
    marketplaceContract.initialized,
    walletDetails.address,
    walletDetails.chain.type,
  ]);

  const isLive = useCallback(() => {
    const now = new Date();

    return (
      auction.startDate.getTime() <= now.getTime() &&
      auction.endDate.getTime() > now.getTime()
    );
  }, [auction.endDate, auction.startDate]);

  const hasEnded = useCallback(() => {
    const now = new Date();

    return auction.endDate.getTime() < now.getTime();
  }, [auction.endDate]);

  const handlePlaceBid = useCallback(
    async (auctionId: BigNumberish, bid: string) => {
      if (!walletDetails.address) {
        return;
      }

      try {
        const allowance = await starsContract.allowance(
          walletDetails.address,
          marketplaceContract.address,
        );

        if (parseFloat(allowance) < parseFloat(bid)) {
          await starsContract.approve(
            marketplaceContract.address,
            constants.MaxUint256,
          );
        }
        await marketplaceContract.placeBid(auctionId, bid);

        window.location.reload();
        toast.success('Bid Placed!');
      } catch (error) {
        setModal(
          BidModal(
            auctionId,
            metadata.name,
            metadata.owner.address,
            metadata.imageUrl,
            auction.bid,
            walletDetails.balance.stars.formatted,
            auction.endDate,
            handlePlaceBid,
          ),
        );
        throw error;
      }
    },
    [walletDetails.address, starsContract, marketplaceContract.address],
  );

  const onPressPlaceBid = (auctionId: BigNumberish) => {
    if (!authenticated) {
      setModal(
        OkModal('Please login to place bid.', () => history.push('/login')),
      );
      return;
    }

    if (!walletDetails.address) {
      setModal(ConnectWalletModal());
      return;
    }

    if (isLive()) {
      setModal(
        BidModal(
          auctionId,
          metadata.name,
          metadata.owner.address,
          metadata.imageUrl,
          auction.bid,
          walletDetails.balance.stars.formatted,
          auction.endDate,
          handlePlaceBid,
        ),
        true,
      );
      return;
    }

    // this means it has not started
    if (!hasEnded()) {
      setModal(OkModal('A bid cannot be placed as auction has not started'));
      return;
    }

    if (auction.isHighestBidder) {
      setModal(
        WonAuctionModal(
          metadata.name,
          metadata.owner.address,
          metadata.description,
          metadata.imageUrl,
        ),
      );
      return;
    }

    setModal(OkModal('A bid cannot be placed as auction has ended'));
  };

  const isEth = walletDetails.chain.id === WEB3_CONFIG.chains.eth.id;

  if (loading && isEth) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <CircularProgress className={classes.loader} size={100} />
      </div>
    );
  }

  const handleClickGetMoreStars = () => {
    // setModal(createBuyStarsModal(), true);
  };

  return (
    <div className={styles.auctionPageContainer}>
      <AuctionComponent
        auctionId={auction.auctionId}
        isLoggedIn={authenticated}
        image={metadata.imageUrl}
        title={metadata.name}
        description={metadata.description}
        currentBid={auction.bid}
        onPressPlaceBid={onPressPlaceBid}
        onClickGetMoreStars={handleClickGetMoreStars}
        walletBalance={walletDetails.balance.stars.formatted}
        seller={metadata.owner.address}
        livestream={LIVE_STREAM}
        startDate={auction.startDate}
        endDate={auction.endDate}
        isHighestBidder={auction.isHighestBidder}
        winner={auction.winnerAddress}
        isLive={isLive()}
        hasEnded={hasEnded()}
      />
    </div>
  );
};
